<template>
  <div :class="$style.wrap">
    <!-- 加载阶段：图标切换 + 进度条 -->
    <div v-if="step === 'loading'" :class="$style.content">
      <!-- 图标切换动画 -->
      <div :class="$style.switcher">
        <div :class="$style['switcher-wrap']">
          <img
            v-for="(src, i) in icons"
            :key="i"
            :src="src"
            :class="[i === currentIcon && $style.active]"
          />
        </div>
      </div>
      <!-- 进度条 -->
      <div>
        <div :class="$style.track">
          <div :class="$style.bar" :style="{ width: progress + '%' }"></div>
        </div>
        <p :class="$style.text">加载中：{{ Math.floor(progress) }}%</p>
      </div>
    </div>
    <!-- 开场动画阶段 -->
    <template v-if="step === 'animation'">
      <div :class="$style.animation">
        <img :src="currentFrameSrc" :class="$style['frame-img']" />
      </div>
      <!-- 动画结束后的选项卡入口 -->
      <div v-if="showActions" :class="$style.actions">
        <!-- 回归福利按钮 -->
        <button
          :class="[
            $style['action-btn'],
            $style['action-welfare'],
            breathActive === 1 && $style['breath']
          ]"
          @click="onSelect(1)"
        />
        <!-- 限定大奖按钮 -->
        <button
          :class="[
            $style['action-btn'],
            $style['action-lottery'],
            breathActive === 2 && $style['breath']
          ]"
          @click="onSelect(2)"
        />
      </div>
      <!-- 角色名字标签 -->
      <div v-if="showActions" :class="$style['role-name']">
        <!-- TODO: 替换为角色名字标签图片资源 -->
      </div>
    </template>
  </div>
</template>

<script>
const ICON_COUNT = 5;
const STORAGE_KEY = "intro_video_watched";
const ANIMATION_FPS = 30;

// 通过 require.context 加载开场动画帧
const frameContext = require.context("./images/opening", false, /\.jpg$/);
const frameKeys = frameContext
  .keys()
  .sort((a, b) => parseInt(a.match(/\d+/)[0]) - parseInt(b.match(/\d+/)[0]));
const FRAME_COUNT = frameKeys.length;
const FRAME_URLS = frameKeys.map(frameContext);
// 动画总时长（毫秒）
const ANIMATION_DURATION = (FRAME_COUNT / ANIMATION_FPS) * 1000;

export default {
  name: "TheLoading",
  props: {
    dataReady: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      step: "loading", // 'loading' | 'animation'
      progress: 0,
      currentIcon: 0,
      showActions: false,
      breathActive: 1, // 当前呼吸动效的按钮：1-回归福利 2-限定大奖
      currentFrameIndex: 0,
      framesLoaded: 0,
      framesReady: false
    };
  },
  computed: {
    icons() {
      return Array.from({ length: ICON_COUNT }, (_, i) =>
        require(`./images/icon-${i + 1}.png`)
      );
    },
    currentFrameSrc() {
      return FRAME_URLS[this.currentFrameIndex] || "";
    }
  },
  mounted() {
    this.startProgress();
    this.startIconSwitch();
    this.preloadFrames();
  },
  methods: {
    /** 3秒匀速推进到 99%，走完后尝试进入动画阶段 */
    startProgress() {
      const duration = 3000;
      const interval = 30;
      const step = 100 / (duration / interval);
      this._progressTimer = setInterval(() => {
        this.progress = Math.min(99, this.progress + step);
        if (this.progress >= 99) {
          clearInterval(this._progressTimer);
          this.tryPlayAnimation();
        }
      }, interval);
    },
    startIconSwitch() {
      this._iconTimer = setInterval(() => {
        this.currentIcon = (this.currentIcon + 1) % ICON_COUNT;
      }, 800);
    },
    /** 预加载所有动画帧 */
    preloadFrames() {
      FRAME_URLS.forEach(src => {
        const img = new Image();
        img.onload = img.onerror = () => {
          this.framesLoaded++;
          if (this.framesLoaded >= FRAME_COUNT) {
            this.framesReady = true;
            this.tryPlayAnimation();
          }
        };
        img.src = src;
      });
    },
    /** 进度、数据和帧都就绪时进入动画阶段 */
    tryPlayAnimation() {
      if (!this.dataReady || !this.framesReady || this.progress < 99) return;
      // 非首次访问，直接进入主内容
      const hasWatched = localStorage.getItem(STORAGE_KEY);
      if (hasWatched) {
        this.$emit("complete");
      } else {
        this.step = "animation";
        this.startAnimation();
      }
    },
    /** 使用 requestAnimationFrame 逐帧播放 */
    startAnimation() {
      this._animStartTime = null;
      this._animFrameId = requestAnimationFrame(this.animateFrame);
    },
    animateFrame(timestamp) {
      if (!this._animStartTime) this._animStartTime = timestamp;
      const elapsed = timestamp - this._animStartTime;
      const frameInterval = 1000 / ANIMATION_FPS;
      const frameIndex = Math.min(
        Math.floor(elapsed / frameInterval),
        FRAME_COUNT - 1
      );
      this.currentFrameIndex = frameIndex;

      // 动画接近尾声时显示操作按钮（与原视频在9秒处显示一致）
      if (!this.showActions && elapsed >= ANIMATION_DURATION - 3000) {
        this.showActions = true;
      }

      if (frameIndex >= FRAME_COUNT - 1) {
        this.onAnimationEnded();
        return;
      }
      this._animFrameId = requestAnimationFrame(this.animateFrame);
    },
    onAnimationEnded() {
      this.showActions = true;
      this.startBreathSwitch();
    },
    startBreathSwitch() {
      this._breathTimer = setInterval(() => {
        this.breathActive = this.breathActive === 1 ? 2 : 1;
      }, 1500);
    },
    onSelect(tab) {
      localStorage.setItem(STORAGE_KEY, "1");
      this.$emit("complete", tab);
    }
  },
  watch: {
    dataReady() {
      if (this.dataReady) {
        this.tryPlayAnimation();
      }
    }
  },
  beforeDestroy() {
    clearInterval(this._progressTimer);
    clearInterval(this._iconTimer);
    clearInterval(this._breathTimer);
    if (this._animFrameId) {
      cancelAnimationFrame(this._animFrameId);
    }
  }
};
</script>

<style lang="less" module>
.wrap {
  .background-fill("./images/loading-bg.jpg", cover, center);

  position: fixed;
  top: 0;
  left: 50%;
  right: auto;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: @base-width-rem;
  max-width: 100%;
  transform: translateX(-50%);
  background-color: #faeed0;
}

.content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 10vh;
}

.switcher {
  .size(8.4rem, 10.56rem);
  .background-fill("./images/switcher-bg.png");

  &-wrap {
    .size(3.55rem, 3.77rem);

    position: relative;
  }

  img {
    .size(100%);

    position: absolute;
    top: 1.9rem;
    left: 2.45rem;
    opacity: 0;
    transition: opacity 0.2s ease;

    &.active {
      opacity: 1;
    }
  }
}

.track {
  .size(7.76rem, 0.27rem);
  .background-fill("./images/track-bg.png");
}

.bar {
  .size(100%);

  position: relative;
  background: url("./images/bar-bg.png") no-repeat;
  background-size: 7.76rem 0.27rem;

  &::after {
    .size(1.53rem, 1.18rem);
    .background-fill("./images/bar-indicator.png");

    content: "";
    position: absolute;
    top: -0.65rem;
    right: -0.5rem;
    pointer-events: none;
  }
}

.text {
  margin-top: 0.3rem;
  color: #a17812;
  text-align: center;
}

.animation {
  .size(100%);

  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
}

.frame-img {
  .size(100%);

  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
}

.actions {
  position: absolute;
  bottom: 4rem;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  justify-content: center;
  animation: slide-up 1s ease-out;
}

.action-btn {
  .size(5.76rem, 2.11rem);

  position: relative;
  margin: 0 -0.3rem;
}

.action-welfare {
  .background-fill("./images/welfare-btn.png");
}

.action-lottery {
  .background-fill("./images/lottery-btn.png");
}

.breath {
  animation: breath 1.5s ease-in-out;
}

@keyframes breath {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.role-name {
  .size(2.5rem, 6.48rem);
  .background-fill("./images/role-name.png");

  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
  animation: fade-in 1s ease-out;
  pointer-events: none;
}

@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(1.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
